import { useCallback, useMemo } from 'react';
import { Column, Row } from 'react-table';
import { Box, HStack, Text } from '@chakra-ui/react';
import { DataTable } from '@gamma/ui/data-table';
import { LoadingSpinner } from '@gamma/ui/progress';
import { i18n } from '@investigator/shared/localization';
import { useDateRangeQueryString } from '@investigator/shared/hooks';
import { MISSING_DATA_PLACEHOLDER } from '@investigator/shared/constants';
import { getEntityInstanceFromDetection } from '@investigator/shared/utils';
import { SeverityBreakdownBarChart } from '../SeverityBreakdownBarChart';
import { SeverityScore } from '@investigator/shared/components';
import { EntityAnalysisPopover } from '../EntityAnalysisPopover';
import { AIInsightsPopover } from './AIInsightsPopover';
import { useEntityTableContext } from '../../context/EntityTableContext';
import { useEntitiesContext } from '../../context';
import { useAgenticTriageSettings, useBarChartWidthScaling } from '../../hooks';
import {
  AIInsights,
  AIInsightVerdict,
  DetectionCountsByMitreTactic,
  DetectionCountsByStatus,
  DetectionSummaryInsights,
} from '../../types';
import { getInsightDetectionSummary } from '@investigator/shared/utils/getDetectionSummary';
import { TriageStatusPopover } from './TriageStatusPopover';
import { AgenticTriageBanner } from '../AgenticTriageBanner';
import { getAIInsightVerdictDisplay } from '../../utils/helpers';
import { MitreTacticsPopover } from './MitreTacticsPopover';
import { NotesPopover } from './NotesPopover';
import { Panel } from '@gamma/ui/layout';
import { ChatGPTIcon } from '../ChatGPTIcon';

// Default sort for the table
const DEFAULT_TABLE_SORT = [{ id: 'max_severity', desc: true }];

enum Columns {
  AI_INSIGHTS = 'ai_insights',
  MAX_SEVERITY = 'max_severity',
  ENTITY_NAME = 'entity_name',
  ENTITY_LOCALITY = 'entity_locality',
  MOST_SEVERE_ALERT_CATEGORY = 'most_severe_alert_category',
  DETECTION_COUNTS_BY_SEVERITY_LEVEL = 'detection_counts_by_severity_level',
  DETECTION_COUNTS_BY_MITRE_TACTICS = 'detection_counts_by_mitre_tactics',
  DETECTION_COUNTS_BY_STATUS = 'detection_counts_by_status',
  COMMENT_COUNT = 'comment_count',
}

export const HighestRiskEntitiesTable = () => {
  const {
    tableData,
    isLoading,
    pageCount,
    onFetchData,
    sortBy,
    totalItems,
    pageSize,
    pageIndex,
  } = useEntityTableContext();
  const { analysisModal } = useEntitiesContext();
  const { agenticTriageEnabled } = useAgenticTriageSettings();
  const { sevenDayStart, sevenDayEnd } = useDateRangeQueryString();

  // This table ALWAYS uses a 7-day range, not the URL date range
  // The hook now provides stable values via useRef
  const memoizedSevenDay = useMemo(
    () => ({
      start: sevenDayStart,
      end: sevenDayEnd,
    }),
    [sevenDayStart, sevenDayEnd],
  );

  // Calculate widths with fixed global scale (defined in hook)
  const barChartWidths = useBarChartWidthScaling(tableData);

  // Enrich table data with width information
  // When widths change, the data objects change, forcing React Table to re-render
  const tableDataWithWidths = useMemo(() => {
    return tableData.map((row, index) => ({
      ...row,
      _chartWidth: barChartWidths[index]?.widthPx,
    }));
  }, [tableData, barChartWidths]);

  const columnLocales = i18n.pages.entities.highestRiskEntitiesTable.columns;

  const handleRowClick = useCallback(
    (rowOrEntity: Row<DetectionSummaryInsights> | DetectionSummaryInsights) => {
      const entity =
        'original' in rowOrEntity ? rowOrEntity.original : rowOrEntity;
      const detectionSummary = getInsightDetectionSummary(entity);
      const entityInstance = getEntityInstanceFromDetection(detectionSummary);
      analysisModal.onOpen({
        ...entityInstance,
        start: memoizedSevenDay.start,
        end: memoizedSevenDay.end,
        requireSingleDetectionForSuppression: true,
      });
    },
    [analysisModal, memoizedSevenDay.start, memoizedSevenDay.end],
  );

  const columns = useMemo<Column<DetectionSummaryInsights>[]>(
    () => [
      {
        Header: columnLocales.score,
        accessor: Columns.MAX_SEVERITY as const,
        Cell: ({ value }: { value: number | null }) => (
          <SeverityScore score={value ?? 0} />
        ),
        minWidth: 80,
        maxWidth: 100,
        align: 'center',
        disableSortBy: true,
      },
      ...(agenticTriageEnabled
        ? [
            {
              accessor: Columns.AI_INSIGHTS as const,
              Header: () => (
                <HStack
                  spacing={2}
                  display="inline-flex"
                  verticalAlign="text-top"
                >
                  <ChatGPTIcon boxSize={5} />
                  <Text>{columnLocales.aiInsights}</Text>
                </HStack>
              ),
              minWidth: 120,
              disableSortBy: true,
              Cell: ({
                value,
                row,
              }: {
                value: AIInsights | null;
                row: { original: DetectionSummaryInsights };
              }) => {
                if (
                  !value?.ai_insight_verdict ||
                  !Object.values(AIInsightVerdict).includes(
                    value.ai_insight_verdict as AIInsightVerdict,
                  )
                ) {
                  return <Text>{MISSING_DATA_PLACEHOLDER}</Text>;
                }

                return (
                  <AIInsightsPopover
                    entityName={row.original.entity_name || ''}
                    description={value.short_description || ''}
                    lastUpdated={
                      value.created_timestamp ?? MISSING_DATA_PLACEHOLDER
                    }
                    onViewAnalysis={() => handleRowClick(row.original)}
                  >
                    <Text color="text.link">
                      {getAIInsightVerdictDisplay(value.ai_insight_verdict)}
                    </Text>
                  </AIInsightsPopover>
                );
              },
            },
          ]
        : []),
      {
        Header: columnLocales.entity,
        accessor: Columns.ENTITY_NAME,
        minWidth: 150,
        disableSortBy: true,
        Cell: ({ row }: { row: { original: DetectionSummaryInsights } }) => (
          // Stop propagation to prevent row click when interacting with popover
          <Box onClick={(e) => e.stopPropagation()}>
            <EntityAnalysisPopover
              detectionSummary={getInsightDetectionSummary(row.original)}
              requireSingleDetectionForSuppression={true}
              start={memoizedSevenDay.start}
              end={memoizedSevenDay.end}
            />
          </Box>
        ),
      },
      {
        Header: columnLocales.locality,
        accessor: Columns.ENTITY_LOCALITY,
        minWidth: 100,
        disableSortBy: true,
        Cell: ({ value }: { value: string | null }) => (
          <Text casing={'capitalize'}>
            {!!value?.toLowerCase() && value?.toLowerCase() !== 'na'
              ? value?.toLowerCase()
              : MISSING_DATA_PLACEHOLDER}
          </Text>
        ),
      },
      {
        Header: columnLocales.highestSeverityCategory,
        accessor: Columns.MOST_SEVERE_ALERT_CATEGORY,
        minWidth: 175,
        disableSortBy: true,
      },
      {
        Header: columnLocales.detectionSeverity,
        accessor: Columns.DETECTION_COUNTS_BY_SEVERITY_LEVEL,
        minWidth: 175,
        disableSortBy: true,
        Cell: ({
          row,
        }: {
          row: {
            original: DetectionSummaryInsights & { _chartWidth?: number };
            index: number;
          };
        }) => (
          <SeverityBreakdownBarChart
            severityLevelCounts={
              row.original.detection_counts_by_severity_level || {
                low: 0,
                medium: 0,
                high: 0,
              }
            }
            tenant_entity={row.original.tenant_entity || ''}
            tenant={row.original.tenant || ''}
            containerWidthPx={row.original._chartWidth}
            selectedEntityStart={memoizedSevenDay.start}
            selectedEntityEnd={memoizedSevenDay.end}
          />
        ),
      },
      {
        Header: columnLocales.mitreTactics,
        accessor: Columns.DETECTION_COUNTS_BY_MITRE_TACTICS,
        minWidth: 120,
        disableSortBy: true,
        Cell: ({
          value,
          row,
        }: {
          value: DetectionCountsByMitreTactic[] | null;
          row: Row<DetectionSummaryInsights>;
        }) => (
          <MitreTacticsPopover
            mitreTacticCounts={value || []}
            tenantEntity={row.original.tenant_entity || ''}
            entityName={row.original.entity_name || ''}
            tenant={row.original.tenant || ''}
            start={memoizedSevenDay.start}
            end={memoizedSevenDay.end}
          />
        ),
      },
      {
        Header: columnLocales.triageStatus,
        accessor: Columns.DETECTION_COUNTS_BY_STATUS,
        minWidth: 120,
        disableSortBy: true,
        Cell: ({
          value,
          row,
        }: {
          value: DetectionCountsByStatus | null;
          row: Row<DetectionSummaryInsights>;
        }) => {
          const closed = value?.closed ?? 0;
          const open = value?.open ?? 0;
          const total = closed + open;
          if (!value || !total) {
            return <Text color="text.link">--</Text>;
          }
          return (
            <TriageStatusPopover
              value={{ closed, open }}
              entityName={row.original.entity_name || ''}
              tenantId={row.original.tenant || ''}
              tenantEntity={row.original.tenant_entity || ''}
              entityCategory={row.original.entity_category || 'source'}
              start={memoizedSevenDay.start}
              end={memoizedSevenDay.end}
            />
          );
        },
      },
      {
        Header: columnLocales.notes,
        accessor: Columns.COMMENT_COUNT,
        minWidth: 60,
        maxWidth: 60,
        disableSortBy: true,
        Cell: ({
          value,
          row,
        }: {
          value: number | null;
          row: Row<DetectionSummaryInsights>;
        }) => {
          if (!value || value === 0) {
            return (
              <Box onClick={(e) => e.stopPropagation()}>
                <Text color="text.link">0</Text>
              </Box>
            );
          }
          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <NotesPopover
                noteCount={value}
                entityName={row.original.entity_name || ''}
                tenantId={row.original.tenant || ''}
                start={memoizedSevenDay.start}
                end={memoizedSevenDay.end}
              />
            </Box>
          );
        },
      },
    ],
    [
      columnLocales.score,
      columnLocales.entity,
      columnLocales.locality,
      columnLocales.highestSeverityCategory,
      columnLocales.detectionSeverity,
      columnLocales.mitreTactics,
      columnLocales.triageStatus,
      columnLocales.notes,
      columnLocales.aiInsights,
      agenticTriageEnabled,
      memoizedSevenDay.end,
      handleRowClick,
      memoizedSevenDay.start,
    ],
  );

  // Only show full loading spinner on initial load (no data yet)
  // Otherwise keep the table mounted to preserve pagination state
  const initLoading = isLoading && tableData.length === 0;

  return (
    <Box width="100%">
      <AgenticTriageBanner />
      {initLoading ? (
        <LoadingSpinner />
      ) : (
        <Box position="relative">
          {/* Show subtle loading overlay during pagination */}
          {isLoading && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg="whiteAlpha.700"
              _dark={{ bg: 'blackAlpha.200' }}
              zIndex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <LoadingSpinner />
            </Box>
          )}
          <Panel p={0} m={0}>
            <DataTable
              isLined={true}
              size="sm"
              initialState={{
                pageSize,
                pageIndex,
                sortBy: sortBy.length > 0 ? sortBy : DEFAULT_TABLE_SORT,
              }}
              columns={columns}
              data={tableDataWithWidths || []}
              onRowClick={handleRowClick}
              isPaginationManual
              isSortingManual
              pageCount={pageCount}
              itemCount={totalItems}
              onFetchData={onFetchData}
              autoResetPage={false}
              autoResetSortBy={false}
              getRowId={(
                row: DetectionSummaryInsights & { _chartWidth?: number },
                relativeIndex,
              ) => {
                // Include width in row ID to force re-render when widths change
                return `${row.tenant_entity}-${row._chartWidth}-${relativeIndex}`;
              }}
            />
          </Panel>
        </Box>
      )}
    </Box>
  );
};
